/**
 * CanopyToastListener — global listener that turns `canopy-signal` events
 * into branded sonner toasts.
 *
 * Mount once in App.js (inside AuthProvider). Zero props, zero state — the
 * component just subscribes to a `window` CustomEvent.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';
import { CANOPY_SIGNAL_EVENT } from '@/lib/canopy-signals';

const DIMENSION_COLOR = {
  Learning:   'from-teal to-info',
  Execution:  'from-primary to-plum',
  Sales:      'from-gold to-rose',
  Innovation: 'from-info to-plum',
  Networking: 'from-primary to-teal',
  Leadership: 'from-plum to-primary',
};

function CanopyToastCard({ signal }) {
  const color = DIMENSION_COLOR[signal.dimension] || 'from-primary to-info';
  return (
    <div className="flex items-center gap-3 min-w-[240px]" data-testid="canopy-toast">
      <div className={`h-10 w-10 rounded-xl grid place-items-center text-lg bg-gradient-to-br ${color} text-white shadow-md`}>
        <span aria-hidden>{signal.emoji || '🌱'}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-base leading-tight text-foreground">
          <span className="text-primary">+{signal.delta}</span> {signal.dimension}
        </p>
        <p className="text-[11px] text-muted-foreground truncate">{signal.reason || 'Canopy updated'}</p>
      </div>
      <div className="text-[10px] font-grotesk uppercase tracking-[0.16em] text-muted-foreground shrink-0">
        Canopy
      </div>
    </div>
  );
}

export default function CanopyToastListener() {
  useEffect(() => {
    const handler = (evt) => {
      const s = evt?.detail;
      if (!s?.dimension || typeof s.delta !== 'number') return;
      toast.custom((id) => <CanopyToastCard signal={s} />, {
        duration: 3800,
        position: 'top-right',
      });
    };
    window.addEventListener('canopy-signal', handler);
    return () => window.removeEventListener('canopy-signal', handler);
  }, []);
  return null;
}
